import React, { useState } from 'react'
import {
  AreaViewIcon,
  Button,
  SETTINGS_FONT,
  SETTINGS_RADIUS,
  makeStyles,
  Menu,
  MenuItem,
  SurfaceViewIcon,
  Tooltip,
  useMediaQuery
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import classnames from 'classnames'
import { useClientOptions } from '@paintscout/ui-business'
import { getObjectLabels } from '@paintscout/util/builder'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        '@media print': {
          display: 'none'
        }
      },
      // Each view is its own button sitting side-by-side. The active view reads as a solid outlined
      // pill; the inactive view is plain text. (The shared Button `outlined` variant is dashed, so we
      // draw the prototype's solid 0.8px border here instead.)
      toggle: {
        textTransform: 'none',
        // MUI's `button` typography variant is Euclid Bold; the design calls for Inter Regular 13, so
        // take the theme's base family (Inter) rather than the button variant's. (ENG-1362)
        fontFamily: theme.typography.fontFamily,
        fontWeight: 400,
        fontSize: theme.typography.pxToRem(SETTINGS_FONT),
        color: neutral.P60,
        border: '0.8px solid transparent',
        borderRadius: SETTINGS_RADIUS,
        padding: theme.spacing(0.5, 1.25),
        // Icon + label are direct children of the button label — lay them out inline with a small gap.
        '& .MuiButton-label': {
          display: 'inline-flex',
          alignItems: 'center',
          gap: theme.spacing(0.75)
        },
        // The icons hardcode a 16px inner <svg> that ignores fontSize, so size them with width/height
        // (CSS overrides the svg attributes). Stroke-based, so they follow the button's text color.
        '& svg': {
          width: 18,
          height: 18
        },
        '&:hover': {
          backgroundColor: neutral.P97
        }
      },
      selected: {
        color: neutral.P35,
        border: `0.8px solid ${neutral.P90}`,
        backgroundColor: neutral.White,
        '&:hover': {
          backgroundColor: neutral.P97,
          borderColor: neutral.P90
        }
      },
      icon: {
        marginTop: 1,
        marginRight: theme.spacing(0.5)
      },
      // Mobile: the two buttons collapse into one outlined chip carrying the active view's label plus a
      // caret, so the row leaves space for Swap Products beside it.
      dropdown: {
        borderRadius: 999,
        padding: theme.spacing(0.5, 1, 0.5, 1.5)
      },
      dropdownCaret: {
        fontSize: 18,
        marginLeft: theme.spacing(0.25),
        color: neutral.P50
      }
    }
  },
  { name: 'AreaSubstrateToggle' }
)

export interface AreaSubstrateToggleProps {
  view?: string
  disabled?: boolean
  onChange?: (event: React.MouseEvent, view: string) => void
  /** Force the single chip-dropdown rendering (the phone layout) regardless of viewport width. */
  dropdown?: boolean
}

/**
 * The estimateNext Area/Surface view control — two side-by-side buttons (Area View / Surface View),
 * each with its custom icon and label. The active view reads as a solid outlined pill; the inactive
 * view is plain text. When substrate views are disabled (an area total was overridden) the Surface
 * button is disabled and explains why on hover, matching the legacy control.
 *
 * On phones both buttons collapse into a single chip dropdown showing the active view, because the row
 * has to hold Swap Products as well (mobile design). Callers with no room for two buttons regardless of
 * width (e.g. the Archived tab, alongside its section title) can force the same chip via `dropdown`.
 */
export default function AreaSubstrateToggle({ view, disabled, onChange, dropdown }: AreaSubstrateToggleProps) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const isMobile = useMediaQuery('xs') || dropdown
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const objectLabels = getObjectLabels({ options })
  const substrateLabel = `${objectLabels.substrate.value} View`

  const isSubstrate = view === 'substrate'

  const handleSelect = (event: React.MouseEvent, next: string) => {
    onChange?.(event, next)
  }

  if (isMobile) {
    return (
      <div className={classes.root} data-testid="area-substrate-toggle">
        <Button
          variant="text"
          size="small"
          className={classnames(classes.toggle, classes.selected, classes.dropdown)}
          aria-haspopup="true"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        >
          {isSubstrate ? <SurfaceViewIcon className={classes.icon} /> : <AreaViewIcon className={classes.icon} />}
          {isSubstrate ? substrateLabel : 'Area View'}
          <ArrowDropDownIcon className={classes.dropdownCaret} />
        </Button>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem
            selected={!isSubstrate}
            onClick={(event) => {
              setMenuAnchor(null)
              handleSelect(event as React.MouseEvent, 'area')
            }}
          >
            Area View
          </MenuItem>
          {/* Disabled (rather than hidden) for the same reason the desktop button is: an overridden area
              total rules out substrate views. */}
          <MenuItem
            selected={isSubstrate}
            disabled={disabled}
            onClick={(event) => {
              setMenuAnchor(null)
              handleSelect(event as React.MouseEvent, 'substrate')
            }}
          >
            {substrateLabel}
          </MenuItem>
        </Menu>
      </div>
    )
  }

  const surfaceButton = (
    <Button
      variant="text"
      size="small"
      className={classnames(classes.toggle, { [classes.selected]: isSubstrate })}
      disabled={disabled}
      aria-pressed={isSubstrate}
      onClick={(event) => handleSelect(event, 'substrate')}
    >
      <SurfaceViewIcon className={classes.icon} />
      {substrateLabel}
    </Button>
  )

  return (
    <div className={classes.root} data-testid="area-substrate-toggle">
      <Button
        variant="text"
        size="small"
        className={classnames(classes.toggle, { [classes.selected]: !isSubstrate })}
        aria-pressed={!isSubstrate}
        onClick={(event) => handleSelect(event, 'area')}
      >
        <AreaViewIcon className={classes.icon} />
        Area View
      </Button>
      {disabled ? (
        <Tooltip
          content={`${objectLabels.substrate.value} views are disabled when an area total has been overridden.`}
          placement="top"
          icon={<></>}
        >
          <span>{surfaceButton}</span>
        </Tooltip>
      ) : (
        surfaceButton
      )}
    </div>
  )
}
